/*
 * rheincool — Storefront-Filtering (HANDOFF.md §5.4)
 * <rc-facets-page data-section-id>:
 * - Filteränderung/Sortierung → Section Rendering API → #CollectionContent
 *   wird ersetzt, die Adresse per history.pushState fortgeschrieben
 * - Chips (data-facet-remove) entfernen Filter ohne Reload
 * - Mobil: Bottom-Sheet mit Fokusfalle + Escape
 * - Tracking: filter_apply / filter_clear
 *
 * Überarbeitet 19.09.2026 (Such-/Filter-Audit Punkt 7). Vorher ging bei
 * jeder Filteränderung alles verloren, was der Kunde gerade in der Hand
 * hatte: Fokus auf BODY, das mobile Sheet sprang an den Anfang (scrollTop
 * 1520 → 0), das Preisfeld wurde nach 600 ms mitten im Tippen ersetzt,
 * „Zurück" verließ die Kategorie, und die neue Trefferzahl sagte niemand an.
 *
 * Grundsätze:
 * - Ereignisse hängen per Delegation am Element selbst. #CollectionContent
 *   wird komplett ersetzt; nichts muss danach neu gebunden werden.
 * - Vor dem Austausch wird der Zustand gemerkt (Fokus, Position des
 *   fokussierten Elements im Bild, Scrollstand des Sheets, offene
 *   „Mehr anzeigen"-Listen, getippter Preis) und danach auf die neuen
 *   Elemente übertragen.
 * - Neue Filter hängen HINTEN an die Adresse. Die Reihenfolge der Parameter
 *   ist damit die Reihenfolge des Kunden — „Letzten Filter entfernen"
 *   nimmt genau den zuletzt gesetzten weg.
 * - Ältere Anfragen werden abgebrochen, sonst überschreibt eine späte
 *   Antwort einen neueren Stand. Fehlerantworten (429, 5xx, fehlender
 *   Container) fallen auf einen normalen Seitenaufruf zurück.
 * - load(url) ohne Optionen ersetzt den Verlaufseintrag wie bisher
 *   (filter-alias.js übersetzt damit die sprechende Adresse); die eigenen
 *   Aufrufe übergeben { push: true }.
 */
class RcFacetsPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.pfad = window.location.pathname;
    this.gerenderteSuche = window.location.search;

    if (!this.eingerichtet) {
      this.eingerichtet = true;
      this.anfrage = null;
      this.laufendeUrl = null;
      this.sheetOffen = false;
      this.geaendertImSheet = false;
      this.inertListe = [];
      this.offeneDetails = new Set();
      this.sortTastatur = false;
      this.sortTimer = null;
      this.ansageTimer = null;
      this.tauscht = false;

      this.onDocKeydown = this.onDocKeydown.bind(this);
      this.onPopstate = this.onPopstate.bind(this);

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.addEventListener('focusout', (e) => this.onFocusout(e));
      this.addEventListener('pointerdown', () => { this.sortTastatur = false; });
      /* toggle blubbert nicht — deshalb in der Capture-Phase */
      this.addEventListener('toggle', (e) => this.onToggle(e), true);

      this.mobil = window.matchMedia('(max-width: 989px)');
      const breiteGeaendert = () => {
        if (!this.mobil.matches && this.sheetOffen) this.closeSheet({ fokus: false });
      };
      if (this.mobil.addEventListener) this.mobil.addEventListener('change', breiteGeaendert);
      else if (this.mobil.addListener) this.mobil.addListener(breiteGeaendert);

      this.reduziert = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    window.addEventListener('popstate', this.onPopstate);
    this.statusEl = this.querySelector('[data-facets-status]') || this.statusAnlegen();
    this.kopfBeobachten();
    this.nachRender();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopstate);
    document.removeEventListener('keydown', this.onDocKeydown);
    /* null setzen, sonst verweigert kopfBeobachten() beim Wiedereinhängen
       einen neuen Beobachter und die Leiste klebt an einer alten Höhe */
    if (this.kopfBeobachter) {
      this.kopfBeobachter.disconnect();
      this.kopfBeobachter = null;
    }
    if (this.anfrage) this.anfrage.abort();
    clearTimeout(this.sortTimer);
    this.sortTimer = null;
    clearTimeout(this.ansageTimer);
    this.inertLoesen();
    /* Der Theme-Editor ersetzt die Section auch bei offenem Sheet — die
       Scroll-Sperre am body darf dann nicht zurückbleiben. */
    if (this.sheetOffen) {
      this.sheetOffen = false;
      this.sheetZustand(this, false);
      document.body.classList.remove('rc-no-scroll');
    }
  }

  /* ---------- Ereignisse ---------- */

  onChange(event) {
    const t = event.target;
    if (t.matches('[data-facet-sort]')) {
      this.sortGeaendert(t);
      return;
    }
    const form = t.closest('[data-facets-form]');
    if (!form) return;
    /* Preisfelder übernehmen ihren Wert erst beim Verlassen oder mit Enter
       (onFocusout/onKeydown) — nie mitten im Tippen. */
    if (t.matches('[data-facet-price]')) return;
    this.ausFormular(form, t);
    if (window.rcTrack) {
      window.rcTrack('filter_apply', { name: t.name, value: t.value });
    }
  }

  onSubmit(event) {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    event.preventDefault();
    this.ausFormular(form);
  }

  onKeydown(event) {
    const t = event.target;
    if (event.key === 'Enter' && t.matches('[data-facet-price]')) {
      event.preventDefault();
      if (this.preisGeaendert(t)) this.ausFormular(t.form);
      return;
    }
    if (t.matches('[data-facet-sort]')) {
      /* Windows ändert das geschlossene Select schon mit den Pfeiltasten und
         meldet jeden Schritt als change. Dann erst nach einer Pause, beim
         Verlassen oder mit Enter laden, statt bei jedem Schritt. */
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
        this.sortTastatur = true;
      } else if (event.key === 'Enter' && this.sortTimer) {
        event.preventDefault();
        this.sortJetzt(t);
      }
    }
  }

  onFocusout(event) {
    if (this.tauscht) return;
    const t = event.target;
    if (t.matches('[data-facet-price]') && t.form && this.preisGeaendert(t)) {
      this.ausFormular(t.form);
    } else if (t.matches('[data-facet-sort]') && this.sortTimer) {
      this.sortJetzt(t);
    }
  }

  onToggle(event) {
    const d = event.target;
    if (!d.matches || !d.matches('details[data-facet-details]')) return;
    if (d.open) this.offeneDetails.add(d.dataset.facetDetails);
    else this.offeneDetails.delete(d.dataset.facetDetails);
  }

  onClick(event) {
    const t = event.target.closest(
      '[data-facets-open], [data-facets-close], [data-facet-remove], [data-facet-remove-last]'
    );
    if (!t || !this.contains(t)) return;

    if (t.matches('[data-facets-open]')) {
      event.preventDefault();
      this.openSheet();
      return;
    }
    if (t.matches('[data-facets-close]')) {
      this.closeSheet();
      return;
    }

    /* Links: Neuer Tab / neues Fenster dem Browser überlassen */
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (t.matches('[data-facet-remove-last]')) {
      event.preventDefault();
      const url = this.urlOhneLetztenFilter() || t.getAttribute('href');
      if (window.rcTrack) window.rcTrack('filter_clear', { url });
      this.load(url, { push: true, fokus: { art: 'zahl' } });
      return;
    }
    if (t.matches('[data-facet-remove]')) {
      event.preventDefault();
      const chips = Array.from(this.querySelectorAll('.rc-collection__chips [data-facet-remove]'));
      const index = chips.indexOf(t);
      const url = this.urlAusLink(t.getAttribute('href'));
      if (window.rcTrack) window.rcTrack('filter_clear', { url: t.getAttribute('href') });
      this.load(url, { push: true, fokus: index === -1 ? { art: 'zahl' } : { art: 'chip', index } });
    }
  }

  onPopstate() {
    if (window.location.pathname !== this.pfad) return;
    /* Reine Anker-Sprünge (#CollectionContent) ändern die Suche nicht */
    if (window.location.search === this.gerenderteSuche) return;
    this.load(window.location.pathname + window.location.search, { push: false });
  }

  onDocKeydown(event) {
    if (!this.sheetOffen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSheet();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  /* ---------- Sortierung ---------- */

  sortGeaendert(select) {
    clearTimeout(this.sortTimer);
    this.sortTimer = null;
    if (this.sortTastatur) {
      this.sortTimer = setTimeout(() => this.sortJetzt(select), 700);
      return;
    }
    this.sortAnwenden(select);
  }

  sortJetzt(select) {
    clearTimeout(this.sortTimer);
    this.sortTimer = null;
    this.sortAnwenden(select);
  }

  sortAnwenden(select) {
    const paare = [];
    let gesetzt = false;
    const standard = select.dataset.defaultSort;
    /* Die Voreinstellung der Collection wird ohne ?sort_by geladen — dann ist
       die Seite wieder „unberührt" und das Schaufenster greift. */
    const ohne = standard && select.value === standard;
    new URLSearchParams(this.basisSuche()).forEach((wert, name) => {
      if (name === 'page' || name === 'section_id') return;
      if (name === 'sort_by') {
        if (!ohne && !gesetzt) paare.push([name, select.value]);
        gesetzt = true;
        return;
      }
      paare.push([name, wert]);
    });
    if (!ohne && !gesetzt) paare.push(['sort_by', select.value]);
    this.load(window.location.pathname + this.abfrage(paare), { push: true });
  }

  /* ---------- Adressen bauen ---------- */

  /* Läuft gerade eine Anfrage, ist IHRE Adresse der Stand, auf dem die
     nächste Änderung aufbaut — sonst ginge z. B. eine eben gewählte
     Sortierung verloren, wenn der Kunde gleich danach einen Filter setzt. */
  basisSuche() {
    return this.laufendeUrl ? new URL(this.laufendeUrl, window.location.origin).search : window.location.search;
  }

  abfrage(paare) {
    const p = new URLSearchParams();
    paare.forEach(([name, wert]) => p.append(name, wert));
    const s = p.toString();
    return s ? `?${s}` : '';
  }

  /*
   * Formularstand → Adresse. Was schon in der Adresse steht, behält seinen
   * Platz; neue Werte kommen hinten dazu. Parameter, die das Formular nicht
   * kennt (type, Kampagnen-Parameter, ein ausgeblendeter Filter), bleiben
   * stehen — früher fiel z. B. type=product der Suche dabei weg.
   */
  urlAusFormular(form) {
    /* Versteckte Felder (q, sort_by) dienen nur dem Absenden ohne JS. Mit JS
       ist die Adresse maßgeblich — die Sortierung wechselt außerhalb des
       Formulars, das versteckte Feld hinkt bis zum nächsten Rendern nach. */
    const gesteuert = new Set();
    const versteckt = new Set();
    Array.from(form.elements).forEach((el) => {
      if (!el.name) return;
      if (el.type === 'hidden') versteckt.add(el.name);
      else gesteuert.add(el.name);
    });
    const offen = [];
    new FormData(form).forEach((wert, name) => {
      const w = String(wert).trim();
      if (w !== '' && !versteckt.has(name)) offen.push([name, w]);
    });
    const paare = [];
    new URLSearchParams(this.basisSuche()).forEach((wert, name) => {
      if (name === 'page' || name === 'section_id') return;
      if (!gesteuert.has(name)) {
        paare.push([name, wert]);
        return;
      }
      const i = offen.findIndex(([n, w]) => n === name && w === wert);
      if (i !== -1) {
        paare.push(offen[i]);
        offen.splice(i, 1);
      }
    });
    offen.forEach((paar) => paare.push(paar));
    return window.location.pathname + this.abfrage(paare);
  }

  /*
   * Chip-/Rücksetz-Link → Adresse mit der Reihenfolge der aktuellen Adresse.
   * Shopifys url_to_remove ordnet die Parameter neu; übernommen wird nur,
   * WELCHE Filter bleiben. Nicht-Filter-Parameter (Sortierung, Suchbegriff,
   * type) bleiben stehen — „Alle zurücksetzen" nimmt nur die Filter weg.
   */
  urlAusLink(href) {
    const ziel = new URL(href, window.location.origin);
    const bleibt = [];
    ziel.searchParams.forEach((wert, name) => {
      if (name !== 'page' && name !== 'section_id') bleibt.push([name, wert]);
    });
    const paare = [];
    new URLSearchParams(window.location.search).forEach((wert, name) => {
      if (name === 'page' || name === 'section_id') return;
      const i = bleibt.findIndex(([n, w]) => n === name && w === wert);
      if (i !== -1) {
        paare.push(bleibt[i]);
        bleibt.splice(i, 1);
      } else if (!name.startsWith('filter.') && !bleibt.some(([n]) => n === name)) {
        paare.push([name, wert]);
      }
    });
    bleibt.forEach((paar) => paare.push(paar));
    return ziel.pathname + this.abfrage(paare);
  }

  /* Letzter Filter-Parameter der Adresse = zuletzt gesetzter Filter. Der
     Preis zählt als EIN Filter (gte und lte gehen gemeinsam). */
  urlOhneLetztenFilter() {
    const eintraege = [];
    new URLSearchParams(window.location.search).forEach((wert, name) => {
      if (name !== 'page' && name !== 'section_id') eintraege.push([name, wert]);
    });
    let letzter = -1;
    eintraege.forEach(([name], i) => {
      if (name.startsWith('filter.')) letzter = i;
    });
    if (letzter === -1) return null;
    const preis = eintraege[letzter][0].startsWith('filter.v.price.');
    const rest = eintraege.filter(([name], i) => (preis ? !name.startsWith('filter.v.price.') : i !== letzter));
    return window.location.pathname + this.abfrage(rest);
  }

  /* ausloeser: das geänderte Feld. Safari fokussiert Checkboxen beim
     Anklicken nicht — dann dient es als Anker für Position und Fokus. */
  ausFormular(form, ausloeser = null) {
    this.load(this.urlAusFormular(form), { push: true, ausloeser });
  }

  preisGeaendert(input) {
    return input.value.trim() !== input.defaultValue.trim();
  }

  /* Getippter, noch nicht übernommener Preis — etwa wenn der Kunde direkt
     auf „X Produkte anzeigen" tippt, ohne das Feld zu verlassen. */
  preisUebernehmen() {
    const form = this.querySelector('[data-facets-form]');
    if (!form) return;
    const offen = Array.from(form.querySelectorAll('[data-facet-price]')).some((i) => this.preisGeaendert(i));
    if (offen) this.ausFormular(form);
  }

  /* ---------- Laden und Austauschen ---------- */

  async load(url, { push = false, fokus = null, ausloeser = null } = {}) {
    const container = this.querySelector('#CollectionContent');
    const ziel = new URL(url, window.location.origin);
    ziel.hash = '';
    ziel.searchParams.delete('section_id');
    const anzeige = ziel.pathname + ziel.search;
    if (!container) {
      window.location.assign(anzeige);
      return;
    }
    /* Dieselbe Adresse läuft schon (z. B. Preisfeld verlassen UND „Anzeigen"
       getippt) — kein zweites Mal anfragen. */
    if (this.anfrage && this.laufendeUrl === anzeige) return;
    if (this.anfrage) this.anfrage.abort();

    const anfrage = new AbortController();
    this.anfrage = anfrage;
    this.laufendeUrl = anzeige;
    if (this.sheetOffen) this.geaendertImSheet = true;
    const abruf = new URL(ziel.href);
    abruf.searchParams.set('section_id', this.sectionId);
    this.ladeZustand(true);

    try {
      const antwort = await fetch(abruf.pathname + abruf.search, { signal: anfrage.signal });
      if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
      const text = await antwort.text();
      if (anfrage.signal.aborted) return;
      const frisch = new DOMParser().parseFromString(text, 'text/html').querySelector('#CollectionContent');
      if (!frisch) throw new Error('#CollectionContent fehlt in der Antwort');

      this.tauschen(container, frisch, fokus, ausloeser);

      const aktuell = window.location.pathname + window.location.search;
      if (push && anzeige !== aktuell) {
        history.pushState({ rcFacets: true }, '', anzeige);
      } else {
        history.replaceState(history.state, '', anzeige);
      }
      this.gerenderteSuche = ziel.search;
      this.nachRender();
      this.ansagen();
    } catch (fehler) {
      if (fehler && fehler.name === 'AbortError') return;
      console.warn('[rc-facets]', fehler);
      window.location.assign(anzeige);
    } finally {
      if (this.anfrage === anfrage) {
        this.anfrage = null;
        this.laufendeUrl = null;
        this.ladeZustand(false);
      }
    }
  }

  ladeZustand(an) {
    const container = this.querySelector('#CollectionContent');
    if (!container) return;
    container.classList.toggle('is-loading', an);
    if (an) container.setAttribute('aria-busy', 'true');
    else container.removeAttribute('aria-busy');
  }

  tauschen(container, frisch, fokusVorgabe, ausloeser) {
    this.tauscht = true;
    try {
      /* 1. Zustand merken. Fokus nur dann übernehmen, wenn er in diesem
            Bereich lag oder nirgends (BODY) — hat der Kunde inzwischen z. B.
            ins Suchfeld gewechselt, bleibt er dort. */
      const aktiv = document.activeElement;
      const hier = aktiv && aktiv !== document.body && this.contains(aktiv);
      const nirgends = !aktiv || aktiv === document.body;
      let quelle = null;
      if (hier) quelle = aktiv;
      else if (nirgends && ausloeser && ausloeser.isConnected && this.contains(ausloeser)) quelle = ausloeser;
      const schluessel = hier || nirgends ? fokusVorgabe || this.fokusSchluessel(quelle) : null;
      const anker = this.ankerTop(quelle);
      const sheetAlt = this.querySelector('.rc-facets__sheet');
      const sheetScroll = sheetAlt ? sheetAlt.scrollTop : 0;
      const fensterY = window.scrollY;
      const getippt = aktiv && this.contains(aktiv) && aktiv.matches('input[type="number"], input[type="text"]') &&
        aktiv.value !== aktiv.defaultValue ? aktiv.value : null;
      /* Offene „Mehr anzeigen"-Listen aus dem DOM lesen, nicht nur aus
         toggle-Ereignissen: Eine vom Server geöffnete Liste (aktiver Wert
         dahinter) meldet ihr toggle, bevor dieses Skript (defer) lauscht.
         Wählte der Kunde dort den Wert ab, klappte die Liste zu und der
         Fokus fiel auf BODY (Review 19.09.2026, Chromium). */
      container.querySelectorAll('details[data-facet-details]').forEach((d) => {
        if (d.open) this.offeneDetails.add(d.dataset.facetDetails);
        else this.offeneDetails.delete(d.dataset.facetDetails);
      });

      /* 2. Zustand ins neue Markup schreiben, BEVOR es eingesetzt wird —
            sonst liefe die Einblend-Animation des Sheets erneut an */
      if (this.sheetOffen) this.sheetZustand(frisch, true);
      frisch.querySelectorAll('details[data-facet-details]').forEach((d) => {
        if (this.offeneDetails.has(d.dataset.facetDetails)) d.setAttribute('open', '');
      });

      /* 3. Austauschen */
      if (this.sheetOffen) this.inertLoesen();
      container.innerHTML = frisch.innerHTML;
      if (this.sheetOffen) this.inertSetzen();

      /* 4. Wiederherstellen */
      const sheetNeu = this.querySelector('.rc-facets__sheet');
      if (sheetNeu) sheetNeu.scrollTop = sheetScroll;
      if (!this.sheetOffen && Math.abs(window.scrollY - fensterY) > 1) this.fensterSprung(fensterY);

      const ziel = this.fokusFinden(schluessel);
      if (ziel) {
        /* Liegt das Ziel jetzt in einer zugeklappten Liste (z. B. weil es
           nach hinten gerutscht ist), die Liste öffnen — ein Element in
           einem geschlossenen details nimmt keinen Fokus an. */
        const zu = ziel.matches('summary') ? null : ziel.closest('details:not([open])');
        if (zu) {
          zu.open = true;
          if (zu.dataset.facetDetails) this.offeneDetails.add(zu.dataset.facetDetails);
        }
        if (getippt !== null && ziel.matches('input') && schluessel && schluessel.art === 'id') {
          ziel.value = getippt;
        }
        ziel.focus({ preventScroll: true });
        /* Das fokussierte Element bleibt an seiner Stelle im Bild, auch wenn
           darüber Gruppen erscheinen oder verschwinden. */
        if (anker !== null && ziel.getClientRects().length) {
          const delta = ziel.getBoundingClientRect().top - anker;
          if (Math.abs(delta) > 1) {
            if (this.sheetOffen && sheetNeu && sheetNeu.contains(ziel)) sheetNeu.scrollTop += delta;
            else if (!this.sheetOffen) this.fensterSprung(window.scrollY + delta);
          }
        }
      }
    } finally {
      this.tauscht = false;
    }
  }

  /* base.css setzt scroll-behavior: smooth auf html — Korrekturen nach dem
     Austausch sollen aber springen, nicht sichtbar gleiten. */
  fensterSprung(y) {
    try {
      window.scrollTo({ top: y, left: window.scrollX, behavior: 'instant' });
    } catch (fehler) {
      window.scrollTo(0, y);
    }
  }

  fokusSchluessel(el) {
    if (!el || el === document.body || !this.contains(el)) return null;
    if (el.matches('.rc-collection__chips [data-facet-remove]')) {
      const chips = Array.from(this.querySelectorAll('.rc-collection__chips [data-facet-remove]'));
      return { art: 'chip', index: chips.indexOf(el) };
    }
    if (el.matches('[data-facets-open]')) return { art: 'selektor', selektor: '[data-facets-open]' };
    if (el.matches('.rc-facets__sheet-close')) return { art: 'selektor', selektor: '.rc-facets__sheet-close' };
    if (el.matches('.rc-facets__sheet-apply')) return { art: 'selektor', selektor: '.rc-facets__sheet-apply' };
    if (el.matches('#ProductCount')) return { art: 'zahl' };
    const gruppe = el.closest('[data-facet-param]');
    const param = gruppe ? gruppe.dataset.facetParam : null;
    if (el.matches('summary')) return param ? { art: 'summary', param } : null;
    if (el.id) return { art: 'id', id: el.id, param };
    if (el.name) return { art: 'name', name: el.name, wert: el.value, param };
    return null;
  }

  fokusFinden(s) {
    if (!s) return null;
    const gruppe = (param) => {
      if (!param) return null;
      return Array.from(this.querySelectorAll('[data-facet-param]')).find((g) => g.dataset.facetParam === param) || null;
    };
    switch (s.art) {
      case 'chip': {
        const chips = Array.from(this.querySelectorAll('.rc-collection__chips [data-facet-remove]'));
        return chips[s.index] || chips[s.index - 1] || this.zahlElement();
      }
      case 'zahl':
        return this.zahlElement();
      case 'selektor':
        return this.querySelector(s.selektor);
      case 'summary': {
        const g = gruppe(s.param);
        return (g && g.querySelector('summary')) || this.zahlElement();
      }
      case 'id': {
        const el = document.getElementById(s.id);
        if (el && this.contains(el)) return el;
        break;
      }
      case 'name': {
        const el = Array.from(this.querySelectorAll('[name]')).find(
          (e) => e.name === s.name && e.value === s.wert && !e.disabled
        );
        if (el) return el;
        break;
      }
      default:
        return null;
    }
    /* Das Element gibt es nicht mehr (Wert ohne Treffer) → erstes Feld seiner
       Gruppe, sonst die Trefferzahl. Nie auf BODY fallen lassen. */
    const g = gruppe(s.param);
    const ersatz = g && Array.from(g.querySelectorAll('input:not([disabled]), summary')).find((e) => e.getClientRects().length);
    return ersatz || this.zahlElement();
  }

  ankerTop(el) {
    if (!el || el === document.body || !this.contains(el) || !el.getClientRects().length) return null;
    return el.getBoundingClientRect().top;
  }

  zahlElement() {
    const zahl = this.querySelector('#ProductCount');
    if (zahl && !zahl.hasAttribute('tabindex')) zahl.setAttribute('tabindex', '-1');
    return zahl;
  }

  /* Nach jedem Rendern: ARIA-Zustände und der Link „Letzten Filter entfernen" */
  nachRender() {
    this.querySelectorAll('[data-facets-open]').forEach((knopf) => {
      knopf.setAttribute('aria-expanded', String(this.sheetOffen));
      if (!knopf.hasAttribute('aria-controls') && this.querySelector('#rc-facets')) {
        knopf.setAttribute('aria-controls', 'rc-facets');
      }
    });
    const letzter = this.urlOhneLetztenFilter();
    if (letzter) {
      this.querySelectorAll('[data-facet-remove-last]').forEach((link) => link.setAttribute('href', letzter));
    }
  }

  statusAnlegen() {
    const el = document.createElement('span');
    el.className = 'visually-hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.setAttribute('data-facets-status', '');
    this.prepend(el);
    return el;
  }

  /* Neue Trefferzahl vorlesen. Erst leeren, dann setzen — sonst bliebe eine
     gleich lautende Zahl („48 Treffer" nach einem Filter ohne Wirkung) stumm. */
  ansagen() {
    const zahl = this.querySelector('#ProductCount');
    if (!zahl || !this.statusEl) return;
    const text = zahl.textContent.replace(/\s+/g, ' ').trim();
    this.statusEl.textContent = '';
    clearTimeout(this.ansageTimer);
    this.ansageTimer = setTimeout(() => {
      this.statusEl.textContent = text;
    }, 150);
  }

  /* ---------- Bottom-Sheet (mobil) ---------- */

  /* Rolle dialog nur, solange das Sheet offen ist: Auf dem Desktop ist
     dasselbe Element eine gewöhnliche Seitenleiste. */
  sheetZustand(wurzel, offen) {
    const facets = wurzel.querySelector('#rc-facets');
    const sheet = wurzel.querySelector('.rc-facets__sheet');
    if (facets) facets.classList.toggle('is-open', offen);
    if (sheet) {
      if (offen) {
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'true');
        if (sheet.dataset.facetsTitel) sheet.setAttribute('aria-labelledby', sheet.dataset.facetsTitel);
      } else {
        sheet.removeAttribute('role');
        sheet.removeAttribute('aria-modal');
        sheet.removeAttribute('aria-labelledby');
      }
    }
    wurzel.querySelectorAll('[data-facets-open]').forEach((knopf) => knopf.setAttribute('aria-expanded', String(offen)));
  }

  openSheet() {
    const facets = this.querySelector('#rc-facets');
    if (!facets || this.sheetOffen) return;
    this.sheetOffen = true;
    this.geaendertImSheet = false;
    this.sheetZustand(this, true);
    document.body.classList.add('rc-no-scroll');
    this.inertSetzen();
    document.addEventListener('keydown', this.onDocKeydown);
    const erstes = facets.querySelector('.rc-facets__sheet-close') || this.fokussierbare()[0];
    if (erstes) erstes.focus();
  }

  closeSheet({ fokus = true } = {}) {
    if (!this.sheetOffen) return;
    this.preisUebernehmen();
    this.sheetOffen = false;
    this.sheetZustand(this, false);
    document.body.classList.remove('rc-no-scroll');
    this.inertLoesen();
    document.removeEventListener('keydown', this.onDocKeydown);

    /* Hat sich im Sheet etwas geändert und steht der Kunde tief in der Liste,
       an den Anfang der Ergebnisse — sonst sähe er mitten in einer neuen
       Liste weiter. */
    if (this.geaendertImSheet) {
      this.geaendertImSheet = false;
      const container = this.querySelector('#CollectionContent');
      if (container && container.getBoundingClientRect().top < 0) {
        container.scrollIntoView({ block: 'start', behavior: this.reduziert.matches ? 'auto' : 'smooth' });
      }
    }
    if (fokus) {
      const knopf = this.querySelector('[data-facets-open]');
      if (knopf) knopf.focus({ preventScroll: true });
    }
  }

  fokussierbare() {
    const sheet = this.querySelector('.rc-facets__sheet');
    if (!sheet) return [];
    return Array.from(
      sheet.querySelectorAll(
        'button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.getClientRects().length > 0);
  }

  trapFocus(event) {
    const liste = this.fokussierbare();
    if (!liste.length) return;
    const erstes = liste[0];
    const letztes = liste[liste.length - 1];
    const sheet = this.querySelector('.rc-facets__sheet');
    if (!sheet.contains(document.activeElement)) {
      event.preventDefault();
      erstes.focus();
    } else if (event.shiftKey && document.activeElement === erstes) {
      event.preventDefault();
      letztes.focus();
    } else if (!event.shiftKey && document.activeElement === letztes) {
      event.preventDefault();
      erstes.focus();
    }
  }

  /* Hintergrund für Tastatur und Screenreader sperren: alle Geschwister
     entlang des Wegs vom Sheet bis zum body. Die Live-Region bleibt frei,
     sonst würde die Trefferzahl im offenen Sheet nicht angesagt. */
  inertSetzen() {
    this.inertLoesen();
    const facets = this.querySelector('#rc-facets');
    if (!facets) return;
    const auslassen = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT']);
    let knoten = facets;
    while (knoten && knoten.parentElement && knoten !== document.body) {
      Array.from(knoten.parentElement.children).forEach((nachbar) => {
        if (nachbar === knoten || nachbar === this.statusEl || nachbar.inert || auslassen.has(nachbar.tagName)) return;
        nachbar.inert = true;
        this.inertListe.push(nachbar);
      });
      knoten = knoten.parentElement;
    }
  }

  inertLoesen() {
    this.inertListe.forEach((el) => {
      el.inert = false;
    });
    this.inertListe = [];
  }

  /* ---------- Klebende Leiste (mobil) ---------- */

  /* Die Filter-/Sortierleiste klebt unter dem Kopf. Dessen Höhe wechselt
     beim Scrollen (Kompaktmodus), deshalb gemessen statt geschätzt. */
  kopfBeobachten() {
    const kopf = document.querySelector('.section-rc-header');
    if (!kopf || !('ResizeObserver' in window) || this.kopfBeobachter) return;
    const setzen = () => {
      const lage = window.getComputedStyle(kopf).position;
      const hoehe = lage === 'sticky' || lage === 'fixed' ? Math.round(kopf.getBoundingClientRect().height) : 0;
      this.style.setProperty('--rc-kopf-hoehe', `${hoehe}px`);
    };
    this.kopfBeobachter = new ResizeObserver(setzen);
    this.kopfBeobachter.observe(kopf);
    setzen();
  }
}

if (!customElements.get('rc-facets-page')) {
  customElements.define('rc-facets-page', RcFacetsPage);
}

/* ---------- Rueckweg aus dem Filter-Leerzustand der Suche ----------
   Liefert Shopify fuer den Suchbegriff gar keine search.filters (das
   passiert, sobald der gewaehlte Wert im Ergebnis nicht vorkommt), rendert
   die Suchseite ihren allgemeinen Leerzustand: „Pruefen Sie die
   Schreibweise" — ohne Chip und ohne Rueckweg, obwohl der Filter der Grund
   ist (Live-Pruefung 20.09.2026). Liquid sieht die Query-Parameter nicht,
   deshalb hier: Traegt die Adresse einen filter.-Parameter, erscheint der
   vorbereitete Block mit „Filter zurücksetzen" und der allgemeine Hinweis
   verschwindet. */
(() => {
  const rueckweg = document.querySelector('[data-search-filter-fallback]');
  if (!rueckweg) return;
  let hatFilter = false;
  new URLSearchParams(window.location.search).forEach((_wert, schluessel) => {
    if (schluessel.startsWith('filter.')) hatFilter = true;
  });
  if (!hatFilter) return;
  rueckweg.hidden = false;
  document.querySelectorAll('[data-search-empty-generic]').forEach((el) => {
    el.hidden = true;
  });
})();
